import { lazy, Suspense } from "react";
import { Navigate, Outlet, createBrowserRouter, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { AppShell, AuthLayout } from "@/ui/Layout";
import { Spinner } from "@/ui";
import { LoginPage } from "@/pages/auth/LoginPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmailPage";
import {
  ChangePasswordPage,
  ForgotPage,
  ResetPasswordPage,
  ResetSentPage,
} from "@/pages/auth/PasswordPages";
import { TeamsPage } from "@/pages/TeamsPage";
import { LandingPage } from "@/pages/LandingPage";

// Разделы грузятся по требованию: главная и вход остаются в основном бандле,
// редактор состава и таблицы игроков подтягиваются при первом переходе.
const lazyPage = <T extends Record<string, unknown>>(load: () => Promise<T>, name: keyof T) =>
  lazy(async () => {
    const mod = await loadOrReload(load);
    return { default: mod[name] as React.ComponentType };
  });

const RELOAD_KEY = "picked:chunk-reload";
const RELOAD_TRIES = 2; // вторая на случай, если первая попала в сам деплой
const RELOAD_WINDOW_MS = 60_000;

/** После деплоя у открытой вкладки старые имена файлов страниц, их уже нет на сервере.
 *  Тогда перезагружаем страницу, не больше RELOAD_TRIES раз за минуту: откроется новая
 *  сборка. Дальше обычная ошибка, чтобы не перезагружаться по кругу без сети. */
async function loadOrReload<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch (err) {
    const now = Date.now();
    let tries: number[] = [];
    try {
      tries = (JSON.parse(sessionStorage.getItem(RELOAD_KEY) ?? "[]") as number[]).filter(
        (t) => now - t < RELOAD_WINDOW_MS,
      );
    } catch {
      // хранилище недоступно или испорчено: считаем, что попыток не было
    }
    if (tries.length < RELOAD_TRIES) {
      try {
        sessionStorage.setItem(RELOAD_KEY, JSON.stringify([...tries, now]));
      } catch {
        // без отметки защита от цикла слабее, но перезагрузка всё равно поможет
      }
      window.location.reload();
      return new Promise<T>(() => {}); // страница перезагружается, ждать нечего
    }
    throw err;
  }
}
const ProfilePage = lazyPage(() => import("@/pages/ProfilePage"), "ProfilePage");
const AccountPage = lazyPage(() => import("@/pages/AccountPage"), "AccountPage");
const CreateCompetitionPage = lazyPage(
  () => import("@/pages/CreateCompetitionPage"),
  "CreateCompetitionPage",
);
const CompetitionsPage = lazyPage(() => import("@/pages/CompetitionsPage"), "CompetitionsPage");
const CompetitionPage = lazyPage(() => import("@/pages/CompetitionPage"), "CompetitionPage");
const ManageLeaguePage = lazyPage(() => import("@/pages/ManageLeaguePage"), "ManageLeaguePage");
const MyTeamPage = lazyPage(() => import("@/pages/team/MyTeamPage"), "MyTeamPage");
const ToursPage = lazyPage(() => import("@/pages/team/ToursPage"), "ToursPage");
const TeamPage = lazyPage(() => import("@/pages/team/TeamPage"), "TeamPage");
const PointsPage = lazyPage(() => import("@/pages/team/PointsPage"), "PointsPage");
const PlayersPage = lazyPage(() => import("@/pages/PlayersPage"), "PlayersPage");
const MatchesPage = lazyPage(() => import("@/pages/MatchesPage"), "MatchesPage");
const RulesPage = lazyPage(() => import("@/pages/RulesPage"), "RulesPage");
const NotificationsPage = lazyPage(() => import("@/pages/NotificationsPage"), "NotificationsPage");

function RequireAuth() {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <Spinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return (
    <Suspense fallback={<Spinner />}>
      <Outlet />
    </Suspense>
  );
}

/** Корень: незарегистрированным лендинг, вошедшим «Мои команды» внутри оболочки приложения */
function Root() {
  const { user, ready } = useAuth();
  if (!ready) return <Spinner />;
  if (!user) return <LandingPage />;
  return (
    <AppShell>
      <TeamsPage />
    </AppShell>
  );
}

function PublicOnly() {
  const { user, ready } = useAuth();
  if (!ready) return <Spinner />;
  if (user) return <Navigate to="/" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  { path: "/", element: <Root /> },
  {
    element: <AuthLayout />,
    children: [
      {
        element: <PublicOnly />,
        children: [
          { path: "/login", element: <LoginPage /> },
          { path: "/register", element: <RegisterPage /> },
          { path: "/forgot", element: <ForgotPage /> },
          { path: "/reset-sent", element: <ResetSentPage /> },
        ],
      },
      // Ссылки из писем работают и для вошедшего пользователя
      { path: "/verify-email", element: <VerifyEmailPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: "/profile", element: <ProfilePage /> },
          { path: "/account", element: <AccountPage /> },
          { path: "/profile/password", element: <ChangePasswordPage /> },
          // команда создаётся из соревнования; старая ссылка ведёт в раздел соревнований
          { path: "/new", element: <Navigate to="/competitions" replace /> },
          { path: "/competitions/new", element: <CreateCompetitionPage /> },
          { path: "/competitions", element: <CompetitionsPage /> },
          { path: "/competitions/:competitionId", element: <CompetitionPage /> },
          { path: "/competitions/:competitionId/manage", element: <ManageLeaguePage /> },
          { path: "/entries/:entryId", element: <MyTeamPage /> },
          { path: "/entries/:entryId/tours", element: <ToursPage /> },
          { path: "/entries/:entryId/edit", element: <TeamPage /> },
          { path: "/entries/:entryId/gameweeks/:gameweekId", element: <TeamPage /> },
          { path: "/entries/:entryId/gameweeks/:gameweekId/points", element: <PointsPage /> },
          { path: "/players", element: <PlayersPage /> },
          { path: "/matches", element: <MatchesPage /> },
          { path: "/rules", element: <RulesPage /> },
          { path: "/notifications", element: <NotificationsPage /> },
          { path: "/archive", element: <Navigate to="/profile" replace /> },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
